<template>
  <div class="planet-letter-catch-game">
    <LevelComplete v-if="isGameComplete" />
    <div v-else>
      <div class="game-area">
        <!-- Planet Name Display -->
        <div class="planet-name-display">
          <img
            :src="currentPlanet.image"
            :alt="currentPlanet.name"
          >
          <div class="planet-name-letters">
            <span
              v-for="(letter, index) in currentPlanet.nameLetters"
              :key="index"
              :class="{ 'letter-caught': letter.caught }"
            >
              {{ letter.char }}
            </span>
          </div>
        </div>

        <!-- Letters Falling -->
        <div class="letters-falling">
          <div
            v-for="(letter, index) in fallingLetters"
            :key="index"
            class="falling-letter"
            :style="{
              top: letter.position.y + 'px',
              left: letter.position.x + 'px',
            }"
          >
            {{ letter.char }}
          </div>
        </div>

        <!-- Rocket -->
        <div
          class="rocket"
          :style="{ left: rocketPosition.x + 'px' }"
        >
          <img
            src="https://images.vexels.com/content/150028/preview/space-rocket-kids-illustration-22c0b0.png"
            alt="Rocket"
          >
        </div>
      </div>

      <!-- Message Display -->
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

import EarthImage from "@/assets/planets/Earth.png";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      planets: [
        {
          name: "Mercury",
          image:
            "https://png.pngtree.com/png-clipart/20230801/original/pngtree-mercury-planet-isolated-on-white-background-picture-image_7805941.png",
        },
        {
          name: "Venus",
          image:
            "https://images.vexels.com/media/users/3/205478/isolated/lists/4a42dba3e55a9bcc9764720d87f2ca93-planet-venus-illustration.png",
        },{
          name: "Earth",
          image: EarthImage,
        },
        {
          name: "Mars",
          image:
            "https://www.pngplay.com/wp-content/uploads/4/Mars-PNG-Images-HD.png",
        },
        {
          name: "Jupiter",
          image: "https://cdn-icons-png.flaticon.com/512/124/124558.png",
        },
        {
          name: "Saturn",
          image:
            "https://img.pikbest.com/origin/09/22/96/52MpIkbEsTJDS.png!sw800",
        },
        {
          name: "Uranus",
          image:
            "https://www.pngplay.com/wp-content/uploads/13/Uranus-PNG-HD-Quality.png",
        },
        {
          name: "Neptune",
          image:
            "https://static.vecteezy.com/system/resources/previews/020/004/117/non_2x/neptune-planet-illustration-png.png",
        },
      ],
      currentPlanetIndex: 0,
      currentPlanet: null, // Will be set in created()
      isGameComplete: false,
      message: "",
      selectedVoice: null,
      rocketPosition: { x: 0 },
      rocketWidth: 80,
      fallingLetters: [],
      gameInterval: null,
      maxFallingLetters: 8, // Set the maximum number of falling letters here
    };
  },
  created() {
    this.currentPlanet = this.planets[this.currentPlanetIndex];
    this.setupVoices();
  },
  mounted() {
    this.setupGame();
    const gameArea = this.$el.querySelector(".game-area");
    if (gameArea) {
      gameArea.addEventListener("mousemove", this.onGameAreaMouseMove);
      gameArea.addEventListener("touchmove", this.onGameAreaTouchMove, {
        passive: false,
      });
    }
  },
  beforeUnmount() {
    const gameArea = this.$el.querySelector(".game-area");
    if (gameArea) {
      gameArea.removeEventListener("mousemove", this.onGameAreaMouseMove);
      gameArea.removeEventListener("touchmove", this.onGameAreaTouchMove);
    }
    if (this.gameInterval) {
      clearInterval(this.gameInterval);
    }
  },
  methods: {
    setupGame() {
      this.currentPlanet.nameLetters = this.currentPlanet.name
        .toUpperCase()
        .split("")
        .map((char) => ({ char, caught: false }));

      this.speakOutLoud(this.currentPlanet.name);
      this.resetRocketPosition();
      this.fallingLetters = [];
      this.startFallingLetters();
    },
    resetRocketPosition() {
      const gameArea = this.$el.querySelector(".game-area");
      if (gameArea) {
        const areaWidth = gameArea.clientWidth;
        this.rocketPosition.x = (areaWidth - this.rocketWidth) / 2;
      }
    },
    startFallingLetters() {
      if (this.gameInterval) {
        clearInterval(this.gameInterval);
      }
      this.gameInterval = setInterval(() => {
        this.generateFallingLetter();
        this.updateFallingLetters();
      }, 100);
    },
    generateFallingLetter() {
      const gameArea = this.$el.querySelector(".game-area");
      if (!gameArea) return;

      // Control the maximum number of falling letters
      if (this.fallingLetters.length >= this.maxFallingLetters) return;

      const areaWidth = gameArea.clientWidth;
      const lettersToFall = this.currentPlanet.nameLetters.filter(
        (letter) => !letter.caught
      );
      if (lettersToFall.length === 0) return;
      const randomLetter =
        lettersToFall[Math.floor(Math.random() * lettersToFall.length)];
      const position = {
        x: Math.random() * (areaWidth - 30),
        y: 0,
      };
      this.fallingLetters.push({
        char: randomLetter.char,
        position,
        speed: 2 + Math.random() * 3,
      });
    },
    updateFallingLetters() {
      const gameArea = this.$el.querySelector(".game-area");
      if (!gameArea) return;
      const areaHeight = gameArea.clientHeight;
      this.fallingLetters.forEach((letter) => {
        letter.position.y += letter.speed;
      });
      // Check for collision
      this.checkCollisions();
      // Remove letters that are out of bounds
      this.fallingLetters = this.fallingLetters.filter(
        (letter) => letter.position.y < areaHeight
      );
    },
    checkCollisions() {
      const gameArea = this.$el.querySelector(".game-area");
      const rocketRect = {
        x: this.rocketPosition.x,
        y: gameArea.clientHeight - 100,
        width: this.rocketWidth,
        height: 80,
      };
      this.fallingLetters.forEach((letter, index) => {
        const letterRect = {
          x: letter.position.x,
          y: letter.position.y,
          width: 30,
          height: 30,
        };
        if (this.isColliding(rocketRect, letterRect)) {
          this.onLetterCaught(letter.char);
          this.fallingLetters.splice(index, 1);
        }
      });
    },
    isColliding(rect1, rect2) {
      return !(
        rect1.x > rect2.x + rect2.width ||
        rect1.x + rect1.width < rect2.x ||
        rect1.y > rect2.y + rect2.height ||
        rect1.y + rect1.height < rect2.y
      );
    },
    onLetterCaught(char) {
      // Find an uncaught letter that matches the caught letter
      const matchingLetter = this.currentPlanet.nameLetters.find(
        (letter) => letter.char === char && !letter.caught
      );
      if (matchingLetter) {
        matchingLetter.caught = true;
        // Speak the caught letter in lowercase
        this.speakOutLoud(char.toLowerCase());
        // Play sound
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );
        // Check if all letters have been caught
        if (this.currentPlanet.nameLetters.every((letter) => letter.caught)) {
          this.moveToNextPlanet();
        }
      } else {
        this.message = "❌ Wrong Letter!";
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }
    },
    moveToNextPlanet() {
      this.currentPlanetIndex++;
      if (this.currentPlanetIndex >= this.planets.length) {
        this.isGameComplete = true;
        if (this.gameInterval) {
          clearInterval(this.gameInterval);
        }
        this.playSound(
          "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
        );
        this.speakOutLoud("Great Job!");
        setTimeout(() => {
          this.$router.push("/planets");
        }, 4000);
      } else {
        this.speakOutLoud(this.currentPlanet.name);
        this.message = "✅ Correct! Next Planet!";
        setTimeout(() => {
          this.message = "";
          // Update currentPlanet to the next planet
          this.currentPlanet = this.planets[this.currentPlanetIndex];
          this.setupGame();
        }, 2000);
      }
    },
    onGameAreaMouseMove(event) {
      const gameArea = this.$el.querySelector(".game-area");
      if (!gameArea) return;
      const areaRect = gameArea.getBoundingClientRect();
      const areaWidth = areaRect.width;
      const mouseX = event.clientX - areaRect.left;

      // Update rocket position based on mouse movement
      this.rocketPosition.x = mouseX - this.rocketWidth / 2;

      // Keep the rocket within bounds
      if (this.rocketPosition.x < 0) this.rocketPosition.x = 0;
      if (this.rocketPosition.x > areaWidth - this.rocketWidth)
        this.rocketPosition.x = areaWidth - this.rocketWidth;
    },
    onGameAreaTouchMove(event) {
      event.preventDefault(); // Prevent scrolling
      const gameArea = this.$el.querySelector(".game-area");
      if (!gameArea) return;
      const areaRect = gameArea.getBoundingClientRect();
      const areaWidth = areaRect.width;
      const touch = event.touches[0];
      const touchX = touch.clientX - areaRect.left;

      // Update rocket position based on touch movement
      this.rocketPosition.x = touchX - this.rocketWidth / 2;

      // Keep the rocket within bounds
      if (this.rocketPosition.x < 0) this.rocketPosition.x = 0;
      if (this.rocketPosition.x > areaWidth - this.rocketWidth)
        this.rocketPosition.x = areaWidth - this.rocketWidth;
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    }
  },
};
</script>

<style scoped>
.planet-letter-catch-game {
  text-align: center;
  margin: 20px;
}

.game-area {
  position: relative;
  width: 100%;
  height: 500px;
  background-image: url("https://static.vecteezy.com/system/resources/thumbnails/044/229/340/small_2x/starry-night-sky-space-seamless-pattern-galaxy-shiny-stars-background-fabric-seamless-print-or-textile-background-wrapping-paper-space-pattern-or-wallpaper-with-comet-glowing-constellations-vector.jpg");
  background-size: cover;
  background-position: center;
  overflow: hidden;
  border-radius: 20px;
}

.planet-name-display {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}

.planet-name-display img {
  width: 80px;
  height: 80px;
}

.planet-name-letters {
  margin-top: 10px;
}

.planet-name-letters span {
  font-size: 2rem;
  margin: 0 5px;
  color: gray;
  border-bottom: 2px solid gray;
  display: inline-block;
  width: 30px;
  text-align: center;
}

.planet-name-letters .letter-caught {
  color: yellow;
  border-bottom: 2px solid yellow;
}

.letters-falling {
  position: relative;
  width: 100%;
  height: 100%;
}

.falling-letter {
  position: absolute;
  font-size: 2rem;
  color: white;
  width: 30px;
  height: 30px;
  text-align: center;
}

.rocket {
  position: absolute;
  bottom: 20px;
  width: 80px;
  height: 80px;
  cursor: pointer;
}

.rocket img {
  width: 100%;
  height: 100%;
}

.message {
  font-size: 2rem;
  font-weight: bold;
  margin-top: 20px;
}
</style>